import { DeleteOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { Alert, Button, message, Modal, Space, Table } from 'antd';
import { BasePageComponent } from 'components';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ShortsPresetFormModal from '../components/ShortsPresetFormModal';
import {
  PRESET_EMPTY_DISPLAY,
  PRESET_LANGUAGE_LABELS,
  PRESET_REFRAME_MODE_LABELS,
  PRESET_TABLE_PAGE_SIZE,
  PRESET_TYPE_LABELS,
} from '../constants';
import { formatPresetErrorMessage, shortsPresetService } from '../services/shortsPresetService';
import {
  PresetLanguage,
  PresetReframeMode,
  ShortsPresetInterface,
} from '../types/shortsPresetType';

const renderOptional = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === '' ? PRESET_EMPTY_DISPLAY : String(value);

const renderLanguage = (value: PresetLanguage | null): string =>
  value ? `${PRESET_LANGUAGE_LABELS[value] ?? value} (${value})` : PRESET_EMPTY_DISPLAY;

const ShortsPreset: React.FC = () => {
  const [presets, setPresets] = useState<ShortsPresetInterface[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PRESET_TABLE_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<ShortsPresetInterface | null>(null);

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await shortsPresetService.fetchPresets(
        new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      );
      setPresets(response.results ?? []);
      setTotal(response.count ?? 0);
    } catch (error) {
      message.error(formatPresetErrorMessage(error, 'Failed to load presets'));
      setPresets([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  const handleAdd = () => {
    setEditingPreset(null);
    setModalOpen(true);
  };

  const handleEdit = (preset: ShortsPresetInterface) => {
    setEditingPreset(preset);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setModalOpen(false);
    setEditingPreset(null);
    fetchPresets();
  };

  const handleDelete = (preset: ShortsPresetInterface) => {
    Modal.confirm({
      title: `Delete "${preset.name}"?`,
      // Season.preset is SET_NULL, but Playlist.preset is PROTECT — a preset in use by a
      // playlist cannot be deleted at all. Say both, or the failure looks like a bug.
      content:
        'Seasons currently using this preset will be unlinked; the seasons and their ' +
        'content are not deleted. A preset still linked to a playlist cannot be deleted — ' +
        'clear it on that playlist first.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await shortsPresetService.deletePreset(preset.id);
          message.success('Preset deleted');
          // Stepping back a page avoids landing on an empty last page.
          if (presets.length === 1 && page > 1) {
            setPage(page - 1);
          } else {
            fetchPresets();
          }
        } catch (error) {
          message.error(formatPresetErrorMessage(error, 'Failed to delete preset'));
        }
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '5%' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (value: ShortsPresetInterface['type']) => PRESET_TYPE_LABELS[value] ?? value,
    },
    { title: 'Count', dataIndex: 'count', key: 'count' },
    { title: 'Ratio', dataIndex: 'aspect_ratio', key: 'aspect_ratio' },
    {
      title: 'Duration',
      key: 'duration',
      render: (_: unknown, record: ShortsPresetInterface) =>
        `${record.min_duration}–${record.max_duration}s`,
    },
    {
      title: 'Reframe',
      dataIndex: 'reframe_mode',
      key: 'reframe_mode',
      render: (value: PresetReframeMode) => PRESET_REFRAME_MODE_LABELS[value] ?? value,
    },
    {
      title: 'Subtitle',
      dataIndex: 'subtitle',
      key: 'subtitle',
      render: (value: PresetLanguage | null) => renderLanguage(value),
    },
    {
      title: 'Dub',
      dataIndex: 'dub',
      key: 'dub',
      render: (value: PresetLanguage | null) => renderLanguage(value),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (value: string) => renderOptional(value),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: '10%',
      render: (_: unknown, record: ShortsPresetInterface) => (
        <Space>
          <Button
            icon={<SettingOutlined />}
            onClick={() => handleEdit(record)}
            className="action-btn"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            className="action-btn delete-btn"
          />
        </Space>
      ),
    },
  ];

  return (
    <BasePageComponent>
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <h1 style={{ margin: 0 }}>Shorts Preset</h1>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              New Preset
            </Button>
          </Space>
        </div>

        {/* A preset is a reusable spec, not a feed. The feed lives on the playlist, which is
            also where its URL is copied from — say so here, since the button that used to
            open a feed from this page is gone. */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
          message="A preset is a reusable spec. The work order feed belongs to a playlist."
          description={
            <>
              Link a preset to a <b>bebridge</b> playlist to order shorts; that playlist&apos;s page
              carries the feed URL and viewer. One playlist is one feed — groups needing a different
              order get their own playlist. <Link to="/playlists/">Go to Playlists</Link>
            </>
          }
        />

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={presets}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />

        <ShortsPresetFormModal
          open={modalOpen}
          preset={editingPreset}
          onClose={() => {
            setModalOpen(false);
            setEditingPreset(null);
          }}
          onSaved={handleSaved}
        />
      </div>
    </BasePageComponent>
  );
};

export default ShortsPreset;
